import type { AlertRuleSpec, AlertTrigger, MarketKind, SideView } from "@screener/protocol";
import { amountLessOrEqual, parseAmountField } from "@/lib/amount";
import { AMOUNT_HINT } from "@/lib/labels";

/**
 * Модель формы правила алерта §8 (design.md решения 4, 5). Чистые функции
 * без React: экран держит `RuleForm` в состоянии, а `specFromForm` переводит
 * его в `AlertRuleSpec` перед записью, либо возвращает ошибки полей.
 */

export interface RuleForm {
  trigger: AlertTrigger;
  exchange: string | null;
  market: MarketKind | null;
  base: string;
  side: SideView | null;
  minUsd: string;
  maxUsd: string;
  favoriteOnly: boolean;
  excludeMm: boolean;
  sound: boolean;
  notify: boolean;
  telegram: boolean;
  log: boolean;
}

export interface RuleFormErrors {
  minUsd?: string;
  maxUsd?: string;
  range?: string;
  actions?: string;
}

const RANGE_ERROR = "„От“ больше „до“";
const ACTIONS_ERROR = "Включите хотя бы одно действие";

interface ParsedAmounts {
  errors: RuleFormErrors;
  min: string | null;
  max: string | null;
}

function parseFormAmounts(form: RuleForm): ParsedAmounts {
  const errors: RuleFormErrors = {};
  const min = parseAmountField(form.minUsd);
  const max = parseAmountField(form.maxUsd);
  if (!min.ok) {
    errors.minUsd = AMOUNT_HINT;
  }
  if (!max.ok) {
    errors.maxUsd = AMOUNT_HINT;
  }
  if (min.ok && max.ok && min.value !== null && max.value !== null) {
    if (!amountLessOrEqual(min.value, max.value)) {
      errors.range = RANGE_ERROR;
    }
  }
  return {
    errors,
    min: min.ok ? min.value : null,
    max: max.ok ? max.value : null,
  };
}

/** Проверяет форму без построения `AlertRuleSpec` — для показа ошибок вживую. */
export function validateRuleForm(form: RuleForm): RuleFormErrors {
  const { errors } = parseFormAmounts(form);
  if (!(form.sound || form.notify || form.telegram || form.log)) {
    errors.actions = ACTIONS_ERROR;
  }
  return errors;
}

/** Пустая форма — сохраняемая сразу: звук включён, остальное «любое». */
export function emptyRuleForm(): RuleForm {
  return {
    trigger: "confirmed",
    exchange: null,
    market: null,
    base: "",
    side: null,
    minUsd: "",
    maxUsd: "",
    favoriteOnly: false,
    excludeMm: false,
    sound: true,
    notify: false,
    telegram: false,
    log: false,
  };
}

export function formFromRule(spec: AlertRuleSpec): RuleForm {
  return {
    trigger: spec.trigger,
    exchange: spec.exchange,
    market: spec.market,
    base: spec.base ?? "",
    side: spec.side,
    minUsd: spec.min_usd ?? "",
    maxUsd: spec.max_usd ?? "",
    favoriteOnly: spec.favorite_only,
    excludeMm: spec.exclude_mm,
    sound: spec.sound,
    notify: spec.notify,
    telegram: spec.telegram,
    log: spec.log,
  };
}

export type SpecFromFormResult =
  | { ok: true; spec: AlertRuleSpec }
  | { ok: false; errors: RuleFormErrors };

export function specFromForm(form: RuleForm): SpecFromFormResult {
  const { errors, min, max } = parseFormAmounts(form);
  if (!(form.sound || form.notify || form.telegram || form.log)) {
    errors.actions = ACTIONS_ERROR;
  }
  if (Object.keys(errors).length > 0) {
    return { ok: false, errors };
  }

  const base = form.base.trim();

  return {
    ok: true,
    spec: {
      exchange: form.exchange,
      market: form.market,
      base: base === "" ? null : base.toUpperCase(),
      side: form.side,
      min_usd: min,
      max_usd: max,
      favorite_only: form.favoriteOnly,
      exclude_mm: form.excludeMm,
      trigger: form.trigger,
      sound: form.sound,
      notify: form.notify,
      telegram: form.telegram,
      log: form.log,
    },
  };
}
